import { useEffect, useState } from "react";
import type { PositionResult } from "../../shared/types";
import { api } from "../lib/api";
import { formatMoney, formatPeriod, formatDayMonth } from "../lib/format";
import StatCard from "../components/StatCard";

// "£X set 12 Jul, less £Y since" — makes clear the figure is your entered
// balance minus what's left the account since (spends + bills paid afterwards).
function remainingFundsSub(pos: PositionResult): string {
  // New pay cycle, balance not yet updated: salary carried on as an estimate.
  if (pos.carriedSalaryMinor > 0) {
    const on = pos.asOf ? ` from ${formatDayMonth(pos.asOf)}` : "";
    return `Est. — last balance${on} + ${formatMoney(pos.carriedSalaryMinor)} salary · update to confirm`;
  }
  const balance = pos.balanceMinor ?? 0;
  const remaining = pos.remainingFundsMinor ?? balance;
  const since = balance - remaining; // >0 = money out since the snapshot
  const on = pos.asOf ? ` set ${formatDayMonth(pos.asOf)}` : "";
  if (since > 0) return `${formatMoney(balance)}${on}, less ${formatMoney(since)} since · update if changed`;
  if (since < 0) return `${formatMoney(balance)}${on}, plus ${formatMoney(-since)} in since`;
  return `${formatMoney(balance)}${on} · update if changed`;
}

// "My Position" — a personal command centre for whoever's signed in. Every
// figure is scoped to them (their income, their bills, their account), so
// Patrick and Amber each see only their own side.
export default function MyPosition() {
  const [pos, setPos] = useState<PositionResult | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.position().then(setPos).catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <div className="state state--error" role="alert">Couldn’t load your position: {error}</div>;
  if (!pos) return <div className="state">Loading…</div>;

  const paidCount = pos.bills.filter((b) => b.paid).length;
  const hasBalance = pos.balanceMinor != null;

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <p className="page__eyebrow">My Position</p>
          <h1 className="page__title">{pos.me.name}’s money</h1>
          <p className="page__lede">
            Your command centre for {formatPeriod(pos.period)} — just your side of the household.
          </p>
        </div>
      </div>

      {/* Headline: how much I have to spend — a planned view and a live view. */}
      <section className="stat-grid stat-grid--2">
        <StatCard
          label="After bills this month"
          value={formatMoney(pos.afterBillsMinor)}
          sub="Your earnings − your bills"
          tone={pos.afterBillsMinor >= 0 ? "positive" : "negative"}
        />
        <StatCard
          label="Free to spend right now"
          value={hasBalance ? formatMoney(pos.freeToSpendMinor!) : "—"}
          sub={
            hasBalance
              ? pos.freeToSpendMinor! < 0
                ? "Your bills this month exceed your balance"
                : pos.budgetAllocatedMinor > 0
                  ? `Balance − bills to pay − spends − ${formatMoney(pos.budgetAllocatedMinor)} budget set aside`
                  : "Balance − bills still to pay − spends"
              : "Update your balance to see this"
          }
          accent={!hasBalance || pos.freeToSpendMinor! >= 0}
          tone={hasBalance && pos.freeToSpendMinor! < 0 ? "negative" : undefined}
        />
      </section>

      {/* Supporting figures. */}
      <section className="stat-grid">
        <StatCard label="My earnings" value={formatMoney(pos.earningsMinor)} sub="Income this month" />
        <StatCard
          label="What I have to pay"
          value={formatMoney(pos.toPayMinor)}
          sub={`${formatMoney(pos.paidMinor)} paid · ${formatMoney(pos.leftToPayMinor)} left`}
        />
        <StatCard
          label="Paid already"
          value={formatMoney(pos.paidMinor)}
          sub={`${paidCount} of ${pos.bills.length} bill${pos.bills.length === 1 ? "" : "s"}`}
          tone={pos.paidMinor > 0 ? "positive" : undefined}
        />
        <StatCard
          label="Remaining funds"
          value={hasBalance ? formatMoney(pos.remainingFundsMinor!) : "—"}
          sub={hasBalance ? remainingFundsSub(pos) : "Set your balance to track"}
        />
      </section>

      {/* My recurring bills — a single total that expands into each bill. */}
      <section className="panel position-bills">
        <button className="position-bills__head" onClick={() => setExpanded((e) => !e)} aria-expanded={expanded}>
          <div>
            <span className="position-bills__title">My recurring bills</span>
            <span className="position-bills__sub">
              {pos.bills.length} this month · {formatMoney(pos.toPayMinor)} total
            </span>
          </div>
          <span className={"position-bills__chev" + (expanded ? " is-open" : "")} aria-hidden="true">▾</span>
        </button>
        {expanded && (
          <div className="position-bills__list">
            {pos.bills.length === 0 ? (
              <p className="empty">No bills are assigned to you this month.</p>
            ) : (
              pos.bills.map((b) => (
                <div className="position-bill" key={`${b.id}:${b.occ ?? ""}`}>
                  <div className="position-bill__main">
                    <span className="position-bill__name">
                      {b.name}
                      {b.dueDate && <span className="position-bill__due"> · {formatDayMonth(b.dueDate)}</span>}
                      {b.isPlan && b.provider && <span className="type-tag type-tag--plan">{b.provider}</span>}
                    </span>
                    {b.categoryName && (
                      <span className="chip">
                        <span className="chip__dot" style={{ background: b.categoryColour ?? "#98A2B3" }} />
                        {b.categoryName}
                      </span>
                    )}
                  </div>
                  <div className="position-bill__right">
                    <span className={"type-tag " + (b.paid ? "type-tag--in" : "type-tag--variable")}>
                      {b.paid ? "Paid" : "To pay"}
                    </span>
                    <span className="position-bill__amt">{formatMoney(b.amountMinor)}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </section>
    </div>
  );
}
